import clsx from 'clsx'
import {CUSTOM_SELECT_ALL_VALUE} from 'common/constants'
import React, {FC, useId, useMemo} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import ReactSelect from 'react-select'

import BaseSelect, {SelectionChange} from '../select/BaseSelect'

interface Props extends React.ComponentProps<typeof ReactSelect> {
  value?: any
  label?: string
  className?: string
  required?: boolean
  title?: string
  optionLabel?: string
  optionValue?: string
  helperText?: string
  placeholder?: string
  hideSelectAll?: boolean
  onChange?: (value: any) => void
  returnFullOption?: boolean
}

export const Select: FC<Props> = ({
  value,
  label = '',
  className = '',
  options,
  required = false,
  title,
  optionValue = 'value',
  helperText,
  placeholder,
  hideSelectAll,
  filterOption,
  onChange,
  returnFullOption = false,
  ...restProps
}) => {
  const intl = useIntl()

  const id = useId()
  const labelHasTranslation = label ? !!intl.messages[label] : false
  const placeholderHasTranslation = placeholder ? !!intl.messages[placeholder] : false
  const titleHasTranslation = title ? !!intl.messages[title] : false

  const labelText = label ? (labelHasTranslation ? intl.formatMessage({id: label}) : label) : ''
  const placeholderText = placeholder
    ? placeholderHasTranslation
      ? intl.formatMessage({id: placeholder})
      : placeholder
    : ''

  const handleSelectionChange = (change: SelectionChange) => {
    if (typeof onChange !== 'function') return

    if (restProps.isMulti) {
      const cleanOptions = (Array.isArray(change.options) ? change.options : []).filter(
        (o: any) => o?.value !== CUSTOM_SELECT_ALL_VALUE
      )
      onChange(returnFullOption ? cleanOptions : change.values)
    } else {
      onChange(returnFullOption ? change.options : change.values)
    }
  }

  const renderTooltip = useMemo(() => {
    return (
      <Tooltip id={`tooltip-${id}`}>
        {titleHasTranslation ? intl.formatMessage({id: title}) : title}
      </Tooltip>
    )
  }, [title, id, intl, titleHasTranslation])

  return (
    <>
      {label && (
        <label className={clsx('form-label', {required})} htmlFor={id}>
          {labelText}
          {title && (
            <OverlayTrigger placement='right' overlay={renderTooltip}>
              <div className='badge pe-auto'>
                <i className='bi bi-info-circle-fill'></i>
              </div>
            </OverlayTrigger>
          )}
        </label>
      )}

      <BaseSelect
        id={id}
        options={options}
        currentValue={value}
        onSelectionChange={handleSelectionChange}
        optionValue={optionValue}
        hideSelectAll={hideSelectAll}
        labelText={labelText}
        placeholderText={placeholderText}
        selectClassName={className}
        filterOption={filterOption}
        {...restProps}
      />
      {helperText && (
        <small id={`${id}-help-text`} className='form-text text-muted'>
          {helperText}
        </small>
      )}
    </>
  )
}
